// @ts-check
/** @typedef {import('../types/api').InventoryVariant} InventoryVariant */
/** @typedef {import('../types/api').InventoryStats} InventoryStats */
/** @typedef {import('../types/api').StockAdjustment} StockAdjustment */
/** @typedef {import('../types/api').ProductLog} ProductLog */

import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  History,
  Package,
  RefreshCw,
  Search,
  TrendingDown,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import StockCountTab from '../components/StockCountTab';
import TagExportBar from '../components/TagExportBar';
import BulkStockDialog from '../components/inventory/BulkStockDialog';
import BulkActionBar from '../components/ui/BulkActionBar';
import Modal from '../components/ui/Modal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import adminInventoryService from '../services/adminInventoryService';
import { downloadCsv } from '../utils/csv';
import { STOCK_ADJUST_REASONS } from '../utils/stockReasons';

// ─── Action badge colors ────────────────────────────────────────────────────

const ACTION_STYLES = {
  stock_adjusted: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-800 dark:text-blue-300',
    label: 'Stock Adjusted',
  },
  stock_purchased: {
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-800 dark:text-green-300',
    label: 'Purchased',
  },
  stock_restored: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    text: 'text-emerald-800 dark:text-emerald-300',
    label: 'Stock Restored',
  },
  price_changed: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-800 dark:text-amber-300',
    label: 'Price Changed',
  },
  sale_price_added: {
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    text: 'text-orange-800 dark:text-orange-300',
    label: 'Sale Price',
  },
  sale_price_removed: { bg: 'bg-muted', text: 'text-foreground', label: 'Sale Removed' },
  product_created: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    text: 'text-indigo-800 dark:text-indigo-300',
    label: 'Created',
  },
  product_published: {
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-800 dark:text-green-300',
    label: 'Published',
  },
  product_unpublished: { bg: 'bg-muted', text: 'text-foreground', label: 'Unpublished' },
  product_archived: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300', label: 'Archived' },
  product_updated: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-800 dark:text-blue-300',
    label: 'Updated',
  },
  product_deleted: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300', label: 'Deleted' },
  product_restored: {
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-800 dark:text-green-300',
    label: 'Restored',
  },
  variant_created: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    text: 'text-indigo-800 dark:text-indigo-300',
    label: 'Variant Created',
  },
  variant_updated: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-800 dark:text-blue-300',
    label: 'Variant Updated',
  },
  variant_deleted: {
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-800 dark:text-red-300',
    label: 'Variant Deleted',
  },
  sample_pulled: {
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-800 dark:text-purple-300',
    label: 'Sample Pulled',
  },
  sample_returned: {
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-800 dark:text-purple-300',
    label: 'Sample Returned',
  },
  post_linked: {
    bg: 'bg-pink-100 dark:bg-pink-900/40',
    text: 'text-pink-800 dark:text-pink-300',
    label: 'Post Linked',
  },
  post_unlinked: { bg: 'bg-muted', text: 'text-foreground', label: 'Post Unlinked' },
};

const ActionBadge = ({ action }) => {
  const style = ACTION_STYLES[action] || { bg: 'bg-muted', text: 'text-foreground', label: action };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
};

const StockBadge = ({ quantity }) => {
  const cls =
    quantity === 0
      ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
      : quantity <= 2
        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
        : 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300';
  return <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${cls}`}>{quantity} units</span>;
};

// ─── Main Component ─────────────────────────────────────────────────────────

const InventoryPage = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('all');
  const [bulkStockOpen, setBulkStockOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  /** @type {[InventoryStats | null, Function]} */
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Data for each tab
  /** @type {[{items: InventoryVariant[], total: number}, Function]} */
  const [allVariants, setAllVariants] = useState({ items: [], total: 0 });
  /** @type {[InventoryVariant[], Function]} */
  const [lowStockItems, setLowStockItems] = useState([]);
  /** @type {[InventoryVariant[], Function]} */
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  /** @type {[ProductLog[], Function]} */
  const [recentActivity, setRecentActivity] = useState([]);

  // Hanging-tag export picks, keyed by variant_id. Holds the whole variant row so picks survive a new search.
  /** @type {[Record<string, {variant: any, copies: number}>, Function]} */
  const [tagPicks, setTagPicks] = useState({});

  // Modals
  const [adjustmentModal, setAdjustmentModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [productLogModal, setProductLogModal] = useState(null);
  /** @type {[StockAdjustment[], Function]} */
  const [stockHistory, setStockHistory] = useState([]);
  /** @type {[ProductLog[], Function]} */
  const [productLogs, setProductLogs] = useState([]);
  const [adjustmentForm, setAdjustmentForm] = useState({ new_quantity: 0, reason: '', notes: '' });

  const loadStats = useCallback(async () => {
    try {
      const s = await adminInventoryService.getInventoryStats();
      setStats(s);
    } catch (error) {
      showToast('Failed to load stats', 'error');
    }
  }, [showToast]);

  const loadTabData = useCallback(
    async (tab) => {
      try {
        setLoading(true);
        if (tab === 'all') {
          const data = await adminInventoryService.getAllVariants({ search: searchQuery, limit: 200 });
          setAllVariants(data);
        } else if (tab === 'low') {
          const data = await adminInventoryService.getLowStockProducts(2);
          setLowStockItems(Array.isArray(data) ? data : /** @type {any} */ (data).items);
        } else if (tab === 'oos') {
          const data = await adminInventoryService.getOutOfStockProducts();
          setOutOfStockItems(Array.isArray(data) ? data : /** @type {any} */ (data).items);
        } else if (tab === 'activity') {
          const data = await adminInventoryService.getRecentActivity(100);
          setRecentActivity(data);
        }
      } catch (error) {
        showToast('Failed to load inventory data', 'error');
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, showToast],
  );

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
  };

  const handleSearch = () => {
    if (activeTab === 'all') {
      loadTabData('all');
    }
  };

  // ─── Bulk selection (All / Low stock / Out of stock tabs) ──────

  const tabItems = { all: allVariants.items, low: lowStockItems, oos: outOfStockItems }[activeTab] || [];
  const selection = useRowSelection(
    tabItems.map((item) => item.variant_id),
    { resetKey: activeTab },
  );

  const exportSelected = () => {
    const chosen = tabItems.filter((item) => selection.isSelected(item.variant_id));
    downloadCsv('inventory.csv', [
      ['Product', 'Size', 'Color', 'SKU', 'Stock', 'Price'],
      ...chosen.map((i) => [i.product_name, i.size, i.color, i.sku, i.stock_quantity, i.price]),
    ]);
  };

  // ─── Tag export ──────

  const setTagCopies = (item, copies) => {
    setTagPicks((prev) => ({ ...prev, [item.variant_id]: { variant: item, copies } }));
  };

  const tagOnePerVisible = () => {
    setTagPicks((prev) => {
      const next = { ...prev };
      for (const item of allVariants.items) {
        next[item.variant_id] = { variant: item, copies: 1 };
      }
      return next;
    });
  };

  // ─── Adjustment modal ──────

  const openAdjustmentModal = (item) => {
    setAdjustmentModal(item);
    setAdjustmentForm({ new_quantity: item.stock_quantity, reason: '', notes: '' });
  };

  const handleAdjustStock = async () => {
    if (!adjustmentForm.reason.trim()) {
      showToast('Please select a reason', 'error');
      return;
    }
    try {
      await adminInventoryService.adjustStock({
        product_variant_id: adjustmentModal.variant_id,
        new_quantity: parseInt(String(adjustmentForm.new_quantity)),
        reason: adjustmentForm.reason,
        notes: adjustmentForm.notes,
      });
      showToast('Stock adjusted successfully', 'success');
      setAdjustmentModal(null);
      loadTabData(activeTab);
      loadStats();
    } catch (error) {
      showToast(error.message || 'Failed to adjust stock', 'error');
    }
  };

  // ─── History modal ──────

  const openHistoryModal = async (item) => {
    setHistoryModal(item);
    try {
      const history = await adminInventoryService.getStockHistory(item.variant_id);
      setStockHistory(history);
    } catch (error) {
      setStockHistory([]);
    }
  };

  // ─── Product log modal ──────

  const openProductLogModal = async (item) => {
    setProductLogModal(item);
    try {
      const data = await adminInventoryService.getProductLogs(item.product_id);
      setProductLogs(/** @type {any} */ (data).logs || data || []);
    } catch (error) {
      setProductLogs([]);
    }
  };

  // ─── Tabs config ──────

  const tabs = [
    { key: 'all', label: 'All Variants', count: stats?.total_variants || 0 },
    { key: 'low', label: 'Low Stock', count: stats?.low_stock_count || 0 },
    { key: 'oos', label: 'Out of Stock', count: stats?.out_of_stock_count || 0 },
    { key: 'count', label: 'Count', count: null },
    { key: 'activity', label: 'Activity Log', count: null },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Inventory Management</h1>
          <p className="text-muted-foreground mt-1">Track stock levels, manage inventory, and view activity logs</p>
        </div>
        <button
          onClick={() => {
            loadStats();
            loadTabData(activeTab);
          }}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Variants"
            value={stats.total_variants}
            icon={<Package className="text-blue-500" size={28} />}
          />
          <StatCard
            label="Low Stock"
            value={stats.low_stock_count}
            icon={<AlertTriangle className="text-amber-500" size={28} />}
            valueClass="text-amber-600 dark:text-amber-400"
          />
          <StatCard
            label="Out of Stock"
            value={stats.out_of_stock_count}
            icon={<TrendingDown className="text-red-500" size={28} />}
            valueClass="text-red-600 dark:text-red-400"
          />
          <StatCard
            label="Stock Value"
            value={`\u20B9${stats.total_stock_value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
            icon={<Package className="text-green-500" size={28} />}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`flex-1 flex-shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium rounded-lg transition ${
              activeTab === tab.key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className="ml-2 text-xs bg-accent text-muted-foreground px-2 py-0.5 rounded-full">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search (All tab only) */}
      {activeTab === 'all' && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={18} />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Hanging-tag export (All tab only) */}
      {activeTab === 'all' && (
        <TagExportBar
          picks={tagPicks}
          visibleCount={allVariants.items.length}
          onOnePerVisible={tagOnePerVisible}
          onClear={() => setTagPicks({})}
        />
      )}

      {['all', 'low', 'oos'].includes(activeTab) && (
        <BulkActionBar count={selection.count} onClear={selection.clear}>
          <BulkActionBar.Button onClick={() => setBulkStockOpen(true)}>Adjust stock</BulkActionBar.Button>
          <BulkActionBar.Button onClick={exportSelected}>Export CSV</BulkActionBar.Button>
        </BulkActionBar>
      )}

      {/* Stock count (its own data loading, not gated on the page's shared loading state) */}
      {activeTab === 'count' && <StockCountTab />}

      {/* Loading */}
      {activeTab !== 'count' &&
        (loading ? (
          <LoadingScreen />
        ) : (
          <>
            {activeTab === 'all' && (
              <VariantTable
                items={allVariants.items}
                emptyMessage="No variants found"
                onAdjust={openAdjustmentModal}
                onHistory={openHistoryModal}
                onProductLog={openProductLogModal}
                tagPicks={tagPicks}
                onTagCopies={setTagCopies}
                selection={selection}
              />
            )}
            {activeTab === 'low' && (
              <VariantTable
                items={lowStockItems}
                emptyMessage="No low stock items. All products are well-stocked!"
                onAdjust={openAdjustmentModal}
                onHistory={openHistoryModal}
                onProductLog={openProductLogModal}
                selection={selection}
              />
            )}
            {activeTab === 'oos' && (
              <VariantTable
                items={outOfStockItems}
                emptyMessage="No out-of-stock items. All variants have stock."
                onAdjust={openAdjustmentModal}
                onHistory={openHistoryModal}
                onProductLog={openProductLogModal}
                selection={selection}
              />
            )}
            {activeTab === 'activity' && <ActivityLogTab activities={recentActivity} />}
          </>
        ))}

      {/* Modals */}
      {bulkStockOpen && (
        <BulkStockDialog
          ids={selection.selectedIds}
          onClose={() => setBulkStockOpen(false)}
          onDone={() => {
            setBulkStockOpen(false);
            selection.clear();
            loadTabData(activeTab);
            loadStats();
          }}
        />
      )}
      {adjustmentModal && (
        <AdjustmentModal
          item={adjustmentModal}
          form={adjustmentForm}
          setForm={setAdjustmentForm}
          onSubmit={handleAdjustStock}
          onClose={() => setAdjustmentModal(null)}
        />
      )}
      {historyModal && (
        <HistoryModal item={historyModal} history={stockHistory} onClose={() => setHistoryModal(null)} />
      )}
      {productLogModal && (
        <ProductLogModal item={productLogModal} logs={productLogs} onClose={() => setProductLogModal(null)} />
      )}
    </div>
  );
};

// ─── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({ label, value, icon, valueClass = '' }) => (
  <div className="bg-card border border-border rounded-lg p-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`text-2xl font-semibold mt-1 ${valueClass}`}>{value}</p>
      </div>
      {icon}
    </div>
  </div>
);

// ─── Variant Table (shared by All, Low Stock, OOS tabs) ─────────────────────

/**
 * @param {{
 *   items: any[],
 *   emptyMessage: string,
 *   onAdjust: Function,
 *   onHistory: Function,
 *   onProductLog: Function,
 *   tagPicks?: Record<string, {copies: number}>,
 *   onTagCopies?: (item: any, copies: number) => void,
 *   selection?: any,
 * }} props
 */
const VariantTable = ({ items, emptyMessage, onAdjust, onHistory, onProductLog, tagPicks, onTagCopies, selection }) => {
  if (items.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-12 text-center">
        <Package size={48} className="mx-auto text-muted-foreground/70 mb-4" />
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              {selection && (
                <th className="pl-6 py-3">
                  <HeaderCheckbox selection={selection} />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Variant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                SKU
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Stock
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Price
              </th>
              {onTagCopies && (
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Tags
                </th>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {items.map((item) => (
              <tr key={item.variant_id} className="hover:bg-muted/50">
                {selection && (
                  <td className="pl-6 py-4">
                    <RowCheckbox selection={selection} id={item.variant_id} label={`Select ${item.product_name}`} />
                  </td>
                )}
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    {item.image_urls?.[0] ? (
                      <img
                        src={item.image_urls[0]}
                        alt={item.product_name}
                        className="w-10 h-10 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                        <Package size={16} className="text-muted-foreground/50" />
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-foreground">{item.product_name}</div>
                      <div className="text-xs text-muted-foreground">{item.product_slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    {item.size && <span className="text-foreground">Size: {item.size}</span>}
                    {item.size && item.color && <span className="text-muted-foreground/70"> &bull; </span>}
                    {item.color && <span className="text-foreground">Color: {item.color}</span>}
                    {!item.size && !item.color && <span className="text-muted-foreground/70">Default</span>}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">{item.sku || 'N/A'}</td>
                <td className="px-6 py-4">
                  <StockBadge quantity={item.stock_quantity} />
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {item.sale_price ? (
                    <div>
                      <span className="line-through text-muted-foreground/70">
                        {'\u20B9'}
                        {Number(item.price).toLocaleString('en-IN')}
                      </span>
                      <span className="ml-1 text-red-600 dark:text-red-400 font-medium">
                        {'\u20B9'}
                        {Number(item.sale_price).toLocaleString('en-IN')}
                      </span>
                    </div>
                  ) : (
                    <span>
                      {'\u20B9'}
                      {Number(item.price).toLocaleString('en-IN')}
                    </span>
                  )}
                </td>
                {onTagCopies && (
                  <td className="px-6 py-4">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      aria-label={`Tags to print for ${item.product_name} ${item.size || ''}`}
                      value={tagPicks?.[item.variant_id]?.copies || ''}
                      placeholder="0"
                      onChange={(e) => onTagCopies(item, Math.max(0, parseInt(e.target.value, 10) || 0))}
                      className="w-16 px-2 py-1 border border-input rounded-lg text-sm"
                    />
                  </td>
                )}
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onAdjust(item)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-input rounded-md text-xs hover:bg-muted/50"
                      title="Adjust stock"
                    >
                      <Edit size={13} className="mr-1" /> Adjust
                    </button>
                    <button
                      onClick={() => onHistory(item)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-input rounded-md text-xs hover:bg-muted/50"
                      title="Stock history"
                    >
                      <History size={13} className="mr-1" /> History
                    </button>
                    <button
                      onClick={() => onProductLog(item)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-input rounded-md text-xs hover:bg-muted/50"
                      title="Product activity log"
                    >
                      <Clock size={13} className="mr-1" /> Log
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Activity Log Tab ───────────────────────────────────────────────────────

const ActivityLogTab = ({ activities }) => {
  if (activities.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-12 text-center">
        <Clock size={48} className="mx-auto text-muted-foreground/70 mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">No activity yet</h3>
        <p className="text-muted-foreground">Product events will appear here as they happen</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <div className="divide-y divide-border/60">
        {activities.map((activity) => (
          <ActivityRow key={activity.id} activity={activity} />
        ))}
      </div>
    </div>
  );
};

const ActivityRow = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(activity.created_at);

  return (
    <div className="px-6 py-4 hover:bg-muted/50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <ActionBadge action={activity.action} />
            <span className="text-sm font-medium text-foreground">{activity.product_name}</span>
          </div>
          <p className="text-sm text-muted-foreground">{activity.summary}</p>
          {activity.admin_name && <p className="text-xs text-muted-foreground mt-1">by {activity.admin_name}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right text-xs text-muted-foreground">
            <p>{date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            <p>{date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          {activity.details && (
            <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground/70 hover:text-foreground">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>
      </div>
      {expanded && activity.details && (
        <div className="mt-3 p-3 bg-muted/50 rounded-lg text-xs font-mono text-foreground/80 whitespace-pre-wrap">
          {JSON.stringify(activity.details, null, 2)}
        </div>
      )}
    </div>
  );
};

// ─── Adjustment Modal ───────────────────────────────────────────────────────

const AdjustmentModal = ({ item, form, setForm, onSubmit, onClose }) => {
  // Local to this modal instance \u2014 it remounts fresh (isDirty starts false)
  // every time it's opened, since the parent only renders it conditionally.
  const [isDirty, setIsDirty] = useState(false);
  const handleFormChange = (updater) => {
    setIsDirty(true);
    setForm(updater);
  };
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  return (
    <Modal
      maxWidth="md"
      fullHeight={false}
      closeOnBackdrop={false}
      className="rounded-lg shadow-xl"
      hasUnsavedChanges={isDirty}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold">Adjust Stock</h3>
        <button onClick={handleCloseClick} className="text-muted-foreground/70 hover:text-foreground">
          <X size={20} />
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground">{item.product_name}</p>
          <p className="text-sm text-muted-foreground">
            {item.size && `Size: ${item.size}`}
            {item.size && item.color && ' \u2022 '}
            {item.color && `Color: ${item.color}`}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Current Stock: <span className="font-semibold">{item.stock_quantity} units</span>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">New Quantity *</label>
          <input
            type="number"
            min="0"
            value={form.new_quantity}
            onChange={(e) => handleFormChange({ ...form, new_quantity: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          />
          {form.new_quantity !== '' && parseInt(form.new_quantity) !== item.stock_quantity && (
            <p
              className={`text-xs mt-1 font-medium ${parseInt(form.new_quantity) > item.stock_quantity ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
            >
              {parseInt(form.new_quantity) > item.stock_quantity ? '+' : ''}
              {parseInt(form.new_quantity) - item.stock_quantity} units
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Reason *</label>
          <select
            value={form.reason}
            onChange={(e) => handleFormChange({ ...form, reason: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
          >
            <option value="">Select reason</option>
            {STOCK_ADJUST_REASONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Notes (Optional)</label>
          <textarea
            value={form.notes}
            onChange={(e) => handleFormChange({ ...form, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
            placeholder="Additional details..."
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end space-x-3">
        <button onClick={handleCloseClick} className="px-4 py-2 border border-input rounded-lg hover:bg-accent">
          Cancel
        </button>
        <button
          onClick={onSubmit}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          Adjust Stock
        </button>
      </div>
    </Modal>
  );
};

// ─── Stock History Modal ────────────────────────────────────────────────────

const HistoryModal = ({ item, history, onClose }) => (
  <Modal maxWidth="3xl" closeOnBackdrop={false} className="rounded-lg shadow-xl flex flex-col">
    <div className="flex items-center justify-between px-6 py-4 border-b border-border">
      <div>
        <h3 className="text-lg font-semibold">Stock History</h3>
        <p className="text-sm text-muted-foreground">
          {item.product_name} — {item.size && `Size: ${item.size}`}
          {item.size && item.color && ' \u2022 '}
          {item.color && `Color: ${item.color}`}
        </p>
      </div>
      <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
        <X size={20} />
      </button>
    </div>

    <div className="p-6 overflow-y-auto flex-1">
      {history.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No stock adjustment history found</div>
      ) : (
        <div className="space-y-3">
          {history.map((record) => (
            <div key={record.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-md ${
                        record.adjustment > 0
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                      }`}
                    >
                      {record.adjustment > 0 ? '+' : ''}
                      {record.adjustment}
                    </span>
                    <span className="text-sm font-medium text-foreground">{record.reason}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {record.old_quantity} &rarr; {record.new_quantity} units
                  </div>
                  {record.notes && <p className="text-sm text-muted-foreground mt-1">{record.notes}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="text-foreground font-medium">{record.adjusted_by_name}</p>
                  <p className="text-muted-foreground text-xs">
                    {new Date(record.created_at).toLocaleDateString()}{' '}
                    {new Date(record.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end">
      <button onClick={onClose} className="px-4 py-2 border border-input rounded-lg hover:bg-accent">
        Close
      </button>
    </div>
  </Modal>
);

// ─── Product Activity Log Modal ─────────────────────────────────────────────

const ProductLogModal = ({ item, logs, onClose }) => (
  <Modal maxWidth="3xl" closeOnBackdrop={false} className="rounded-lg shadow-xl flex flex-col">
    <div className="flex items-center justify-between px-6 py-4 border-b border-border">
      <div>
        <h3 className="text-lg font-semibold">Product Activity Log</h3>
        <p className="text-sm text-muted-foreground">{item.product_name}</p>
      </div>
      <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
        <X size={20} />
      </button>
    </div>

    <div className="p-6 overflow-y-auto flex-1">
      {logs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No activity log entries found</div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const date = new Date(log.created_at);
            return (
              <div key={log.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <ActionBadge action={log.action} />
                    </div>
                    <p className="text-sm text-foreground">{log.summary}</p>
                    {log.admin_name && <p className="text-xs text-muted-foreground mt-1">by {log.admin_name}</p>}
                    {log.customer_name && (
                      <p className="text-xs text-muted-foreground mt-1">Customer: {log.customer_name}</p>
                    )}
                    {log.order_number && <p className="text-xs text-muted-foreground">Order: #{log.order_number}</p>}
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    <p>{date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end">
      <button onClick={onClose} className="px-4 py-2 border border-input rounded-lg hover:bg-accent">
        Close
      </button>
    </div>
  </Modal>
);

export default InventoryPage;
